import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import '../../../App.css';
import './SignUp.css';
import Footer from "../../Footer";

export default function SignUp() {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [showAdminChoice, setShowAdminChoice] = useState(false);

    const { signup, login, logout, resetPassword, updateUserProfile, signInWithGoogle, currentUser, isAdmin } = useAuth();
    const navigate = useNavigate();

    function handlePostLogin(user) {
        if (user && user.email === 'info@offftheroast.com') {
            setShowAdminChoice(true);
        } else {
            navigate('/');
        }
    }

    async function handleGoogleSignIn() {
        setError('');
        setLoading(true);
        try {
            const result = await signInWithGoogle();
            handlePostLogin(result.user);
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/popup-closed-by-user') {
                setError('Sign-in popup was closed');
            } else if (err.code === 'auth/cancelled-popup-request') {
                // User cancelled, no error needed
            } else {
                setError('Failed to sign in with Google');
            }
        }
        setLoading(false);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!isLogin && password !== confirmPassword) {
            return setError('Passwords do not match');
        }

        if (password.length < 6) {
            return setError('Password must be at least 6 characters');
        }

        setLoading(true);

        try {
            if (isLogin) {
                const result = await login(email, password);
                handlePostLogin(result.user);
            } else {
                await signup(email, password);
                if (name) {
                    await updateUserProfile(name);
                }
                navigate('/');
            }
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/email-already-in-use') {
                setError('An account with this email already exists');
            } else if (err.code === 'auth/invalid-email') {
                setError('Invalid email address');
            } else if (err.code === 'auth/weak-password') {
                setError('Password is too weak');
            } else if (err.code === 'auth/user-not-found') {
                setError('No account found with this email');
            } else if (err.code === 'auth/wrong-password') {
                setError('Incorrect password');
            } else if (err.code === 'auth/invalid-credential') {
                setError('Invalid email or password');
            } else {
                setError('Failed to ' + (isLogin ? 'sign in' : 'create account'));
            }
        }

        setLoading(false);
    }

    async function handleForgotPassword(e) {
        e.preventDefault();
        setError('');
        setMessage('');

        if (!email) {
            return setError('Please enter your email address');
        }

        setLoading(true);

        try {
            await resetPassword(email);
            setMessage('Check your inbox for password reset instructions');
            setShowForgotPassword(false);
        } catch (err) {
            console.error(err);
            if (err.code === 'auth/user-not-found') {
                setError('No account found with this email');
            } else {
                setError('Failed to send password reset email');
            }
        }

        setLoading(false);
    }

    async function handleLogout() {
        setError('');
        try {
            await logout();
        } catch {
            setError('Failed to log out');
        }
    }

    // Admin choice modal
    if (showAdminChoice) {
        return (
            <>
                <div className="admin-choice-overlay">
                    <div className="admin-choice-modal">
                        <h2 className="admin-choice-title">Welcome Back</h2>
                        <p className="admin-choice-subtitle">How would you like to continue?</p>
                        <div className="admin-choice-buttons">
                            <button className="admin-choice-btn admin-choice-personal" onClick={() => { setShowAdminChoice(false); navigate('/'); }}>
                                Personal
                            </button>
                            <button className="admin-choice-btn admin-choice-admin" onClick={() => { setShowAdminChoice(false); navigate('/admin'); }}>
                                Admin
                            </button>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // If user is logged in, show account info
    if (currentUser) {
        return (
            <>
                <div className="login-container">
                    <h1 className="login-title">MY ACCOUNT</h1>
                    <div className="account-info">
                        <div className="account-avatar">
                            {currentUser.displayName ? currentUser.displayName.charAt(0).toUpperCase() : currentUser.email.charAt(0).toUpperCase()}
                        </div>
                        <p className="account-greeting">
                            Welcome back, {currentUser.displayName || currentUser.email}!
                        </p>
                        <p className="account-email">{currentUser.email}</p>
                        <button onClick={handleLogout} className="logout-button">
                            SIGN OUT
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Forgot Password Form
    if (showForgotPassword) {
        return (
            <>
                <div className="login-container">
                    <h1 className="login-title">RESET PASSWORD</h1>
                    {error && <div className="auth-error">{error}</div>}
                    {message && <div className="auth-message">{message}</div>}
                    <form className="login-form" onSubmit={handleForgotPassword}>
                        <label className="input-label">EMAIL</label>
                        <input
                            type="email"
                            className="input-field"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                        <button type="submit" className="login-button" disabled={loading}>
                            {loading ? 'SENDING...' : 'SEND RESET LINK'}
                        </button>
                    </form>
                    <div className="additional-links">
                        <button
                            className="toggle-link"
                            onClick={() => setShowForgotPassword(false)}
                        >
                            Back to Sign In
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    // Login / Register Form
    return (
        <>
            <div className="login-container">
                <h1 className="login-title">{isLogin ? 'LOG IN' : 'CREATE ACCOUNT'}</h1>
                {error && <div className="auth-error">{error}</div>}
                {message && <div className="auth-message">{message}</div>}
                <form className="login-form" onSubmit={handleSubmit}>
                    {!isLogin && (
                        <>
                            <label className="input-label">NAME</label>
                            <input
                                type="text"
                                className="input-field"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Your name (optional)"
                            />
                        </>
                    )}

                    <label className="input-label">EMAIL</label>
                    <input
                        type="email"
                        className="input-field"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    <div className="password-container">
                        <label className="input-label">PASSWORD</label>
                        {isLogin && (
                            <button
                                type="button"
                                className="forgot-link"
                                onClick={() => setShowForgotPassword(true)}
                            >
                                Forgot?
                            </button>
                        )}
                    </div>
                    <input
                        type="password"
                        className="input-field"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />

                    {!isLogin && (
                        <>
                            <label className="input-label">CONFIRM PASSWORD</label>
                            <input
                                type="password"
                                className="input-field"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </>
                    )}

                    <button type="submit" className="login-button" disabled={loading}>
                        {loading ? 'PLEASE WAIT...' : (isLogin ? 'SIGN IN' : 'CREATE ACCOUNT')}
                    </button>

                    <div className="divider">
                        <span>OR</span>
                    </div>

                    <button
                        type="button"
                        className="google-button"
                        onClick={handleGoogleSignIn}
                        disabled={loading}
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continue with Google
                    </button>

                </form>
                <div className="additional-links">
                    <button
                        className="toggle-link"
                        onClick={() => {
                            setIsLogin(!isLogin);
                            setError('');
                            setMessage('');
                        }}
                    >
                        {isLogin ? 'Create account' : 'Already have an account? Sign in'}
                    </button>
                </div>
            </div>
            <Footer />
        </>
    );
}
